// src/services/selfServeEntitlements.js
import axios from 'axios';
import logger from '../utils/logger';

import { getJwtToken } from './jwt.services';
const BASE_URL = process.env.REACT_APP_BASE_URL;
const TIMEOUT_MS = 7000;

export const fetchEntitlements = async (companyId) => {
  // --- 1️⃣ Input Validation ---
  if (typeof companyId !== 'string' || !companyId.trim()) {
    logger.warn({ message: 'fetchEntitlements called with invalid or empty companyId', companyId });
    return [];
  }

  const trimmedCompanyId = companyId.trim();

  // --- 2️⃣ Token & Config Validation ---
  const jwtToken = getJwtToken?.();
  if (!jwtToken) {
    const message = 'JWT token missing for fetchEntitlements';
    logger.error({ message });
    throw new Error(message);
  }

  if (!BASE_URL || typeof BASE_URL !== 'string') {
    const message = 'Service BASE_URL not configured';
    logger.error({ message });
    throw new Error(message);
  }

  // --- 3️⃣ Build API URL ---
  const cleanBase = BASE_URL.replace(/\/+$/, ''); // strip trailing slashes
  const apiUrl = `${cleanBase}/sse/entitlements?companyId=${encodeURIComponent(trimmedCompanyId)}`;
  logger.debug({ message: 'Fetching entitlements', apiUrl });

  const headers = {
    Authorization: `Bearer ${jwtToken}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  try {
    // --- 4️⃣ Make Request ---
    const { data } = await axios.get(apiUrl, { headers, timeout: TIMEOUT_MS });
    console.log({ data })

    // --- 5️⃣ Validate & Normalize Response ---
    if (!data || typeof data !== 'object') {
      logger.warn({ message: 'Unexpected API response format', data });
      return [];
    }

    logger.info({
      message: 'Fetched entitlements successfully',
      data
    });

    return data;
  } catch (err) {
    // --- 6️⃣ Error Handling ---
    const status = err?.response?.status;

    if ([204, 404].includes(status)) {
      logger.warn({ message: 'No entitlements found', status });
      return [];
    }

    if (err.code === 'ECONNABORTED') {
      logger.error({ message: 'Request timed out fetching entitlements', timeout: TIMEOUT_MS });
      throw new Error('Request timed out');
    }

    logger.error({
      message: 'Failed to fetch entitlements',
      error: err.message,
      status,
    });

    throw err;
  }
};


export const fetchBuddyAccounts = async (email) => {
  // --- 1️⃣ Input Validation ---
  if (typeof email !== 'string' || !email.trim()) {
    logger.warn({ message: 'fetchBuddyAccounts called with invalid or empty email', email });
    return [];
  }

  const trimmedEmail = email.trim();

  // --- 2️⃣ Token & Config Validation ---
  const jwtToken = getJwtToken?.();
  if (!jwtToken) {
    const message = 'JWT token missing for fetchBuddyAccounts';
    logger.error({ message });
    throw new Error(message);
  }

  if (!BASE_URL || typeof BASE_URL !== 'string') {
    const message = 'Service BASE_URL not configured';
    logger.error({ message });
    throw new Error(message);
  }

  // --- 3️⃣ Build API URL ---
  const cleanBase = BASE_URL.replace(/\/+$/, ''); // strip trailing slashes
  const apiUrl = `${cleanBase}/sse/buddyAccounts?email=${encodeURIComponent(trimmedEmail)}`;
  logger.debug({ message: 'Fetching buddy accounts', apiUrl });

  const headers = {
    Authorization: `Bearer ${jwtToken}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  try {
    // --- 4️⃣ Make Request ---
    const { data } = await axios.get(apiUrl, { headers, timeout: TIMEOUT_MS });

    // --- 5️⃣ Validate & Normalize Response ---
    if (!data || typeof data !== 'object') {
      logger.warn({ message: 'Unexpected API response format', data });
      return [];
    }

    const buddyAccounts = Array.isArray(data.buddyAccounts) ? data.buddyAccounts : [];

    logger.info({
      message: 'Fetched buddy accounts successfully',
      count: buddyAccounts.length,
    });

    return buddyAccounts;
  } catch (err) {
    // --- 6️⃣ Error Handling ---
    const status = err?.response?.status;

    if ([204, 404].includes(status)) {
      logger.warn({ message: 'No buddy accounts found', status });
      return [];
    }

    if (err.code === 'ECONNABORTED') {
      logger.error({ message: 'Request timed out fetching buddy accounts', timeout: TIMEOUT_MS });
      throw new Error('Request timed out');
    }

    logger.error({
      message: 'Failed to fetch buddy accounts',
      error: err.message,
      status,
    });

    throw err;
  }
};

export const fetchAvailableBundles = async () => {


  const jwtToken = getJwtToken?.();
  if (!jwtToken) {
    const message = 'JWT token missing for fetchBuddyAccounts';
    logger.error({ message });
    throw new Error(message);
  }

  if (!BASE_URL || typeof BASE_URL !== 'string') {
    const message = 'Service BASE_URL not configured';
    logger.error({ message });
    throw new Error(message);
  }

  // --- 3️⃣ Build API URL ---
  const cleanBase = BASE_URL.replace(/\/+$/, ''); // strip trailing slashes
  const apiUrl = `${cleanBase}/sse/selfServeEntitlementBundles`;
  logger.debug({ message: 'Fetching buddy selfServeEntitlementBundles', apiUrl });

  const headers = {
    Authorization: `Bearer ${jwtToken}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  try {
    // --- 4️⃣ Make Request ---
    const { data } = await axios.get(apiUrl, { headers, timeout: TIMEOUT_MS });

    // --- 5️⃣ Validate & Normalize Response ---
    if (!data || typeof data !== 'object') {
      logger.warn({ message: 'Unexpected API response format', data });
      return [];
    }

    const entitlementBundles = data.entitlementBundles;
    logger.info({
      message: 'Fetched entitlement bundles successfully',
      count: entitlementBundles.length,
    });

    return entitlementBundles;
  } catch (err) {
    // --- 6️⃣ Error Handling ---
    const status = err?.response?.status;

    if ([204, 404].includes(status)) {
      logger.warn({ message: 'No entitlement bundles found', status });
      return [];
    }

    if (err.code === 'ECONNABORTED') {
      logger.error({ message: 'Request timed out fetching entitlement bundles', timeout: TIMEOUT_MS });
      throw new Error('Request timed out');
    }

    logger.error({
      message: 'Failed to fetch entitnlement bundles',
      error: err.message,
      status,
    });

    throw err;
  }
};


export const addBundleToCompany = async (companyId, bundleId) => {


  const jwtToken = getJwtToken?.();
  if (!jwtToken) {
    const message = 'JWT token missing for addBundleToCompany';
    logger.error({ message });
    throw new Error(message);
  }

  if (!BASE_URL || typeof BASE_URL !== 'string') {
    const message = 'Service BASE_URL not configured';
    logger.error({ message });
    throw new Error(message);
  }

  // --- 3️⃣ Build API URL ---
  const cleanBase = BASE_URL.replace(/\/+$/, ''); // strip trailing slashes
  const apiUrl = `${cleanBase}/sse/addEntitlementBundleToCompany`;
  logger.debug({ message: 'Adding bundle to the company', apiUrl });

  try {
    // --- 4️⃣ Make Request ---
    const { data } = await axios.post(
      apiUrl,
      { companyId, bundleId },     // ✅ POST body
      {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        timeout: TIMEOUT_MS,
      }
    );

    // --- 5️⃣ Validate & Normalize Response ---
    if (!data || typeof data !== 'object') {
      logger.warn({ message: 'Unexpected API response format', data });
      return [];
    }

    console.log({ data })

    return data
  } catch (err) {
    // --- 6️⃣ Error Handling ---
    const status = err?.response?.status;

    if ([204, 404].includes(status)) {
      logger.warn({ message: 'No entitlement bundles found', status });
      return [];
    }

    if (err.code === 'ECONNABORTED') {
      logger.error({ message: 'Request timed out fetching entitlement bundles', timeout: TIMEOUT_MS });
      throw new Error('Request timed out');
    }

    logger.error({
      message: 'Failed to fetch entitnlement bundles',
      error: err.message,
      status,
    });

    throw err;
  }
};

export const removeBundleFromCompany = async (companyId, bundleId) => {


  const cleanBase = BASE_URL.replace(/\/+$/, ''); // strip trailing slashes
  const apiUrl = `${cleanBase}/sse/removeEntitlementBundleFromCompany`;
  logger.debug({ message: 'Adding bundle to the company', apiUrl });
  const jwtToken = localStorage.getItem("jwtToken");

  try {
    const postData = { companyId, bundleId };
    const { data } = await axios.post(apiUrl, postData, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      timeout: TIMEOUT_MS,
    });
    console.log({ data });

    return data;
  } catch (error) {
    logger?.error?.({
      message: "Error removing bundle from company",
      error: error.response?.data || error.message,
    });
    throw error;
  }
};

export const fetchEntitlementDescriptions = async () => {
  try {
    console.log({ message: 'Fetching entitlement descriptions' });
    const jwtToken = getJwtToken?.();
    if (!jwtToken) {
      const message = 'JWT token missing for fetchEntitlements';
      logger.error({ message });
      throw new Error(message);
    }

    const headers = {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const TIMEOUT_MS = 7000;
    if (!BASE_URL || typeof BASE_URL !== 'string') {
      const message = 'Service BASE_URL not configured';
      logger.error({ message });
      throw new Error(message);
    }

    // --- 3️⃣ Build API URL ---
    const cleanBase = BASE_URL.replace(/\/+$/, ''); // strip trailing slashes
    const apiUrl = `${cleanBase}/company/detail/entitlements/describe`;
    const { data } = await axios.get(apiUrl, { headers, timeout: TIMEOUT_MS });
    console.log({ data })
    return data.data;

  } catch (error) {

  }
}

export const createBuddyAccount = async (data) => {
  try {
    console.log({ message: 'Creating buddy account', data });

    const jwtToken = getJwtToken?.();
    if (!jwtToken) {
      const message = 'JWT token missing for createBuddyAccount';
      logger.error({ message });
      throw new Error(message);
    }

    const headers = {
      Authorization: `Bearer ${jwtToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const TIMEOUT_MS = 7000;
    if (!BASE_URL || typeof BASE_URL !== 'string') {
      const message = 'Service BASE_URL not configured';
      logger.error({ message });
      throw new Error(message);
    }

    // --- 3️⃣ Build API URL ---
    const cleanBase = BASE_URL.replace(/\/+$/, ''); // strip trailing slashes
    const apiUrl = `${cleanBase}/sse/createCompany`;
    const postData = {
      "user": {
        "firstName": data.firstName,
        "lastName": data.lastName,
        "email": data.email
      },
      "company": {
        "name": data.company,
        "country": data.country.val,
      },
    };
    const result = await axios.post(apiUrl, postData, { headers, timeout: TIMEOUT_MS });
    return result.data;

  } catch (error) {
    logger?.error?.({
      message: "Error creating buddy account",
      error: error.response?.data || error.message,
    });
    throw error;
  }
}